import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { IRelationshipList, Relationship } from '../models/relationship';
import { BaseRestService } from './base-rest.service';
import { SubjectTypeService } from './subject-type.service';
import { SubjectType } from '../models/subject-type';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root'})
export class RelationshipService extends BaseRestService<Relationship, IRelationshipList> implements OnDestroy{

  onSubjectTypeChanged: Subscription;

  constructor(http: HttpClient, private subjectTypeService: SubjectTypeService, protected router: Router) {
    super(RelationshipService, http, 'api/relationships', 'relationship', router);

    // enable caching
    this.cacheEnabled = true;

    // when subject type is changed, updated any related relationships
    this.onSubjectTypeChanged = this.subjectTypeService.onItemChanged.subscribe((subjectType) => {
      if (subjectType){
        const s = new SubjectType(subjectType);
        this.cache.filter((i) => i.parentSubjectTypeId === subjectType.id).forEach((item) => item.parentSubjectType = s);
        this.cache.filter((i) => i.childSubjectTypeId === subjectType.id).forEach((item) => item.childSubjectType = s);
      }
    });
  }

  ngOnDestroy(){
    this.onSubjectTypeChanged?.unsubscribe();
  }

  getAll() {
    this.subjectTypeService.getAllData().pipe(take(1)).subscribe((_) => {
      super.getAll();
    });
  }

  // get first subject type that matches name.  Will 404 if id not found
  getRelationships(parentSubjectTypeId: number, childSubjectTypeId: number): Observable<Relationship[]> {
    return this.getAllData().pipe(map((data) => {
      const val = data.filter((x) => (((x.parentSubjectTypeId === parentSubjectTypeId) && (x.childSubjectTypeId === childSubjectTypeId))
      || ((x.parentSubjectTypeId === childSubjectTypeId) && (x.childSubjectTypeId === parentSubjectTypeId))) && (x.hideRecord !== true));
      return val ? val : [];
    }));
  }

  hydrate(item: Relationship): Observable<Relationship> {
    const hydrate$ = [];
    if ((!item.parentSubjectType) || (item.parentSubjectType.id !== item.childSubjectTypeId)){
      hydrate$.push(this.subjectTypeService.getById(item.parentSubjectTypeId).pipe(take(1), map((subjectType) => {
        item.parentSubjectType = subjectType;
        return item;
      })));
    }
    if ((!item.childSubjectType) || (item.childSubjectType.id !== item.childSubjectTypeId)){
      hydrate$.push(this.subjectTypeService.getById(item.childSubjectTypeId).pipe(take(1), map((subjectType) => {
        item.childSubjectType = subjectType;
        return item;
      })));
    }

    if (hydrate$.length > 0){
      return forkJoin(hydrate$).pipe(take(1), map((_) => {
        return item;
      }));
    }
    else{
      return of(item);
    }

  }

  save(entity: Relationship, successMessage?: string) {
    // make sure that any related data objects are 'unset' prior to subject being saved
    const relationship = JSON.parse(JSON.stringify(entity));
    relationship.parentSubjectType = null;
    relationship.childSubjectType = null;
    return super.save(relationship, successMessage);
  }

}
